import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_DISTRIBUTION_CENTER,
  GET_MASTER_DATA_DISTRIBUTION_CENTERS,
  UPDATE_MASTER_DATA_DISTRIBUTION_CENTER,
} from "@src/domain/graphql/masterData";
import {
  MasterDataDistributionCenter,
  type IMasterDataDistributionCenter,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataDistributionCenterDTO,
  GetMasterDataDistributionCentersDTO,
  UpdateMasterDataDistributionCenterDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataDistributionCenterRepository,
  MasterDataDistributionCentersListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapDistributionCenterItem(
  item: IMasterDataDistributionCenter,
): MasterDataDistributionCenter {
  return MasterDataDistributionCenter.from({
    distributionCenterId: item.distributionCenterId,
    purchaserId: item.purchaserId,
    dcCode: item.dcCode,
    dcName: item.dcName,
    createdAt: item.createdAt ?? null,
    purchaserName: item.purchaserName ?? null,
    purchaserKey: item.purchaserKey ?? null,
  });
}

export class MasterDataDistributionCenterRepository
  implements IMasterDataDistributionCenterRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getDistributionCenters(
    params: GetMasterDataDistributionCentersDTO,
  ): Promise<MasterDataDistributionCentersListResult> {
    const { data, error } = await this.client.query<{
      masterDataDistributionCenters: {
        items: IMasterDataDistributionCenter[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_DISTRIBUTION_CENTERS,
      variables: {
        params: {
          search: params.search ?? null,
          purchaserId: params.purchaserId ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataDistributionCenters;
    if (!response) {
      throw new Error("Failed to fetch master data distribution centers");
    }

    return toMasterDataListResult(
      response,
      mapDistributionCenterItem,
      params.page_number,
      params.page_size,
    );
  }

  async createDistributionCenter(
    params: CreateMasterDataDistributionCenterDTO,
  ): Promise<MasterDataDistributionCenter> {
    return this.putDistributionCenter(
      CREATE_MASTER_DATA_DISTRIBUTION_CENTER,
      {
        purchaserId: params.purchaserId,
        dcName: params.dcName,
        dcCode: params.dcCode,
      },
      (data) => data?.createMasterDataDistributionCenter,
      "Failed to create distribution center",
    );
  }

  async updateDistributionCenter(
    params: UpdateMasterDataDistributionCenterDTO,
  ): Promise<MasterDataDistributionCenter> {
    return this.putDistributionCenter(
      UPDATE_MASTER_DATA_DISTRIBUTION_CENTER,
      {
        distributionCenterId: params.distributionCenterId,
        dcName: params.dcName,
        dcCode: params.dcCode,
      },
      (data) => data?.updateMasterDataDistributionCenter,
      "Failed to update distribution center",
    );
  }

  private async putDistributionCenter(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataDistributionCenter?: IMasterDataDistributionCenter;
            updateMasterDataDistributionCenter?: IMasterDataDistributionCenter;
          }
        | null
        | undefined,
    ) => IMasterDataDistributionCenter | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataDistributionCenter> {
    const result = await this.client.mutate<{
      createMasterDataDistributionCenter?: IMasterDataDistributionCenter;
      updateMasterDataDistributionCenter?: IMasterDataDistributionCenter;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapDistributionCenterItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
